import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Button, Title } from "~/components";
import { useEmailVerification } from "~/domains/user/hooks/useEmailVerification";
import { ROUTES } from "~/router";
import AuthBanner, { BannerTypes } from "~/screens/Auth/components/AuthBanner";
import { useRecaptcha } from "~/screens/Auth/hooks/useRecaptcha";
import { useTokenLogin } from "~/screens/Auth/hooks/useTokenLogin";
import { AuthLayout } from "~/screens/Auth/Layout/AuthLayout";
import { usePageTitle } from "~/shared";
import { useUserStore } from "~/stores";
import { errorToast } from "~/ui/admin";
import HighlightedText from "~/ui/common/HighlightedText";
import { ResendEmail } from "../components/ResendEmail";

export const EmailVerified = () => {
  usePageTitle("Email verified");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const error = searchParams.get("error");
  const alreadyVerified = searchParams.get("already_verified");

  const hashedUserId = useUserStore((state) => state.hashedUserId);

  const {
    resend: {
      mutate: resendMutate,
      isPending: resendPending,
      isSuccess: resendSuccess,
    },
  } = useEmailVerification();
  const { handleReCaptchaVerify } = useRecaptcha("emailVerified");

  const handleSuccess = () => {
    navigate(ROUTES.hubHome, { replace: true });
  };

  const { isLoggingIn, loginError, email } = useTokenLogin({
    onSuccess: handleSuccess,
  });

  const handleResend = async () => {
    const reCaptchaToken = await handleReCaptchaVerify();
    if (hashedUserId && reCaptchaToken) {
      resendMutate({ hashedUserId, reCaptchaToken });
    } else {
      errorToast("We couldn't resend the verification email.");
    }
  };

  if (isLoggingIn) {
    return (
      <AuthLayout banner={<AuthBanner type={BannerTypes.accountVerified} />}>
        <section className="flex w-full flex-col items-center justify-center space-y-4">
          <section className="w-full max-w-lg space-y-6">
            <Title>
              Logging you <HighlightedText>in</HighlightedText>...
            </Title>
            <p className="text-indigo mb-3 text-justify">
              Your email has been verified. Please wait a moment.
            </p>
          </section>
        </section>
      </AuthLayout>
    );
  }

  if (error === "expired") {
    return (
      <AuthLayout banner={<AuthBanner type={BannerTypes.codeValidation} />}>
        <section className="flex w-full flex-col items-center justify-center space-y-4">
          <div className="relative w-full max-w-lg">
            <section className="space-y-6">
              <Title>
                This link has <HighlightedText>expired</HighlightedText>
              </Title>
              <p className="text-indigo mb-3 text-justify">
                {email
                  ? `The verification link sent to ${email} has expired.`
                  : "Your verification link has expired."}
              </p>
            </section>

            {hashedUserId && (
              <ResendEmail
                isSuccess={resendSuccess}
                isPending={resendPending}
                onClick={handleResend}
              />
            )}

            <Button asChild size="default" className="mt-6 w-full md:w-auto">
              <Link to={ROUTES.fullLogin}>Go to log in</Link>
            </Button>
          </div>
        </section>
      </AuthLayout>
    );
  }

  if (alreadyVerified || loginError) {
    return (
      <AuthLayout banner={<AuthBanner type={BannerTypes.accountVerified} />}>
        <section className="flex w-full flex-col items-center justify-center space-y-4">
          <section className="w-full max-w-lg space-y-6">
            <Title>
              {loginError ? (
                <>
                  Something went <HighlightedText>wrong</HighlightedText>
                </>
              ) : (
                <>
                  Your email is already{" "}
                  <HighlightedText>verified</HighlightedText>
                </>
              )}
            </Title>
            <p className="text-indigo mb-3 text-justify">
              {loginError
                ? "We couldn't log you in automatically. Please log in manually."
                : "You can log in to access your account."}
            </p>

            <Button asChild size="default" className="w-full md:w-auto">
              <Link to={ROUTES.fullLogin}>Log in</Link>
            </Button>
          </section>
        </section>
      </AuthLayout>
    );
  }

  // Fallback: no params at all
  return (
    <AuthLayout
      banner={<AuthBanner type={BannerTypes.accountVerified} />}
      showSupport
    >
      <section className="flex w-full flex-col items-center justify-center space-y-4">
        <section className="w-full max-w-lg space-y-6">
          <Title>
            Your <HighlightedText>account</HighlightedText> is verified!
          </Title>
          <p className="text-indigo mb-3 text-justify">
            A better menopause experience awaits.
          </p>

          <Button asChild size="default" className="w-full md:w-auto">
            <Link to={ROUTES.fullLogin}>Log in</Link>
          </Button>
        </section>
      </section>
    </AuthLayout>
  );
};
